import {
  AlertCircle,
  AlertTriangle,
  CircleDollarSign,
  Fingerprint,
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Truck,
} from "lucide-react";
import { skipToken } from "@reduxjs/toolkit/query";
import { ReactNode, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Progress,
  useChat,
} from "randmarcomps";
import { FormattedNumber } from "react-intl";
import {
  OrderDetail as ApiOrderDetail,
  useGetV4ManufacturerByRouteManufacturerIdAccountQuery,
  useGetV4ManufacturerByRouteManufacturerIdDocumentsActiveOrderDetailsQuery,
} from "randmar-api-client";
import OpenOrdersGrid from "../components/grids/OpenOrdersGrid";
import CurrencyUtils from "../components/util/CurrencyUtils";
import { DataRefreshBar } from "../components/util/DataRefreshBar";
import InlineSpinner from "../components/util/InlineSpinner";
import ManufacturerContext from "../contexts/ManufacturerContext";
import Path from "../routes/Path";
import { OrderDetail } from "../stores/document/Document";
import { RootStoreContext } from "../stores/rootStore";
import withRoot from "../withRoot";

type OrderStatus = "processing" | "shipped" | "delayed";
type OrderStatusFilter = "all" | OrderStatus;

const getOrderStatus = (order: OrderDetail): OrderStatus => {
  if (order.documentType === "Shipment") {
    return "shipped";
  }
  if (order.documentType === "Order" && order.warehouseCode === "3PLE") {
    return "delayed";
  }
  return "processing";
};

function buildOpenOrdersAssistantPrompt(options: {
  applicationId: string;
  totalLineItems: number;
  visibleLineItems: number;
  searchTerm: string;
  statusFilter: OrderStatusFilter;
  showOnlyBidOrders: boolean;
  showUniqueMpnsOnly: boolean;
}) {
  const search = options.searchTerm.trim();
  const status = options.statusFilter === "all" ? "All statuses" : options.statusFilter;
  const flags = [
    options.showOnlyBidOrders ? "bidsOnly" : null,
    options.showUniqueMpnsOnly ? "uniqueMpnsOnly" : null,
  ].filter(Boolean);

  return [
    "You are assisting with the Randmar Manufacturer Portal Open Orders page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    `Total open line items loaded: ${options.totalLineItems}`,
    `Visible line items (after filtering): ${options.visibleLineItems}`,
    `Current filters: status=${status}${search ? `, search="${search}"` : ""}${flags.length ? `, flags=${flags.join("+")}` : ""}`,
    "",
    "Help me understand what I'm seeing and suggest next actions.",
  ].join("\n");
}

const OrderDetails = () => {
  const { applicationId } = useContext(ManufacturerContext);
  const { warehousesStore } = useContext(RootStoreContext);
  const { sendMessageWithClear } = useChat();
  const previousTitleRef = useRef<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<OrderStatusFilter>("all");
  const [showOnlyBidOrders, setShowOnlyBidOrders] = useState(false);
  const [showUniqueMpnsOnly, setShowUniqueMpnsOnly] = useState(false);
  const statusMessageId = useId();

  const queryArg = applicationId
    ? { routeManufacturerId: applicationId }
    : skipToken;

  const { data: manufacturerAccount } = useGetV4ManufacturerByRouteManufacturerIdAccountQuery(queryArg);

  const {
    data: apiOpenOrders,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetV4ManufacturerByRouteManufacturerIdDocumentsActiveOrderDetailsQuery(queryArg);

  useEffect(() => {
    if (!isFetching && (apiOpenOrders !== undefined || isError)) {
      setLastRefreshedAt(new Date());
    }
  }, [apiOpenOrders, isError, isFetching]);

  useEffect(() => {
    previousTitleRef.current = document.title;
    return () => {
      if (previousTitleRef.current) {
        document.title = previousTitleRef.current;
      }
    };
  }, []);

  const handleRefresh = useCallback(async () => {
    if (applicationId) {
      await refetch();
    }
  }, [applicationId, refetch]);

  const openOrders = useMemo(() => {
    if (!apiOpenOrders) {
      return [] as OrderDetail[];
    }
    return (apiOpenOrders as ApiOrderDetail[]).map(OrderDetail.convertFromApi);
  }, [apiOpenOrders]);

  const activeOrders = useMemo(() =>
    openOrders.slice()
      .sort((a, b) => (b.orderNumber || "").localeCompare(a.orderNumber || "")),
    [openOrders]
  );

  const hasOrders = activeOrders.length > 0;

  const isInitialLoad = !lastRefreshedAt && (isLoading || isFetching) && !isError;

  const titlePrefix = useMemo(() => {
    const parts: string[] = ["Open orders"];

    if (statusFilter !== "all") {
      parts.push(statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1));
    }

    if (showOnlyBidOrders) {
      parts.push("Bids");
    }

    if (showUniqueMpnsOnly) {
      parts.push("Unique MPNs");
    }

    const trimmed = searchTerm.trim();
    if (trimmed) {
      const truncated = trimmed.length > 18 ? `${trimmed.slice(0, 18)}…` : trimmed;
      parts.push(`Search: ${truncated}`);
    }

    return parts.join(" · ");
  }, [searchTerm, showOnlyBidOrders, showUniqueMpnsOnly, statusFilter]);

  useEffect(() => {
    if (!previousTitleRef.current) return;
    document.title = `${titlePrefix} | ${previousTitleRef.current}`;
  }, [titlePrefix]);

  const totals = useMemo(() => {
    return activeOrders.reduce(
      (acc, order) => {
        const amount = order.extendedPrice ?? 0;
        acc.totalValue += amount;

        if (order.documentType === "Shipment") {
          acc.shipped.count += 1;
          acc.shipped.value += amount;
          return acc;
        }

        if (order.documentType === "Order" && order.warehouseCode === "3PLE") {
          acc.delayed.count += 1;
          acc.delayed.value += amount;
          return acc;
        }

        acc.processing.count += 1;
        acc.processing.value += amount;
        return acc;
      },
      {
        processing: { count: 0, value: 0 },
        shipped: { count: 0, value: 0 },
        delayed: { count: 0, value: 0 },
        totalValue: 0,
      }
    );
  }, [activeOrders]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const matchingOrders = useMemo(() => {
    if (!normalizedSearch && statusFilter === "all" && !showOnlyBidOrders) {
      return activeOrders;
    }
    return activeOrders.filter(order => {
      const matchesStatus = statusFilter === "all" || getOrderStatus(order) === statusFilter;
      const matchesBidFilter = !showOnlyBidOrders || Boolean(order.opportunityManufacturerNumber);

      if (!matchesStatus || !matchesBidFilter) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }
      return [
        order.resellerName,
        order.orderNumber,
        order.poNumber,
        order.mpn,
        order.randmarSKU,
        warehousesStore.getWarehouseNameFromCode(order.warehouseCode),
      ].some(value => (value || "").toLowerCase().includes(normalizedSearch));
    });
  }, [activeOrders, normalizedSearch, showOnlyBidOrders, statusFilter, warehousesStore]);

  const filteredOrders = useMemo(() => {
    if (!showUniqueMpnsOnly) {
      return matchingOrders;
    }

    const seenMpns = new Set<string>();
    return matchingOrders.filter(order => {
      const mpnKey = (order.mpn ?? "").trim().toLowerCase();
      if (!mpnKey) {
        return true;
      }
      if (seenMpns.has(mpnKey)) {
        return false;
      }
      seenMpns.add(mpnKey);
      return true;
    });
  }, [matchingOrders, showUniqueMpnsOnly]);

  const matchingValue = useMemo(
    () => matchingOrders.reduce((sum, order) => sum + (order.extendedPrice ?? 0), 0),
    [matchingOrders]
  );

  const statusFilterOptions: { id: OrderStatusFilter; label: string; icon: ReactNode }[] = useMemo(
    () => [
      { id: "all", label: "All statuses", icon: <Sparkles className="h-4 w-4" /> },
      { id: "processing", label: "Processing", icon: <RefreshCw className="h-4 w-4" /> },
      { id: "shipped", label: "Shipped", icon: <Truck className="h-4 w-4" /> },
      { id: "delayed", label: "Delayed", icon: <AlertTriangle className="h-4 w-4" /> },
    ],
    []
  );

  const statusMessage = isFetching
    ? "Refreshing open orders…"
    : isInitialLoad
      ? "Fetching open orders…"
    : filteredOrders.length > 0
      ? showUniqueMpnsOnly
        ? `Showing ${filteredOrders.length} unique ${filteredOrders.length === 1 ? "MPN" : "MPNs"} (from ${matchingOrders.length} ${matchingOrders.length === 1 ? "line item" : "line items"}) totaling ${CurrencyUtils.formatNumber(matchingValue)}.`
        : `Showing ${filteredOrders.length} of ${activeOrders.length} open ${activeOrders.length === 1 ? "line item" : "line items"} totaling ${CurrencyUtils.formatNumber(matchingValue)}.`
      : hasOrders
        ? "No line items match your search or filters."
        : "You are all caught up. We will surface new orders here automatically.";

  const metricCards = useMemo(() => ([
    {
      label: "Total line items",
      count: activeOrders.length,
      amount: totals.totalValue,
      tone: "text-[var(--ops-accent)]",
    },
    {
      label: "Processing",
      count: totals.processing.count,
      amount: totals.processing.value,
      tone: "text-[var(--ops-accent)]",
    },
    {
      label: "Shipped",
      count: totals.shipped.count,
      amount: totals.shipped.value,
      tone: "text-emerald-700",
    },
    {
      label: "Delayed",
      count: totals.delayed.count,
      amount: totals.delayed.value,
      tone: "text-amber-700",
    },
  ]), [
    activeOrders.length,
    totals.delayed.count,
    totals.delayed.value,
    totals.processing.count,
    totals.processing.value,
    totals.shipped.count,
    totals.shipped.value,
    totals.totalValue,
  ]);

  if (!applicationId) {
    return <Progress />;
  }

  const assistantPrompt = buildOpenOrdersAssistantPrompt({
    applicationId,
    totalLineItems: activeOrders.length,
    visibleLineItems: filteredOrders.length,
    searchTerm,
    statusFilter,
    showOnlyBidOrders,
    showUniqueMpnsOnly,
  });

  const marketingResourcesLink = manufacturerAccount?.PublicResourcesLink?.trim();
  const addResourcesLink = marketingResourcesLink && marketingResourcesLink.length > 0
    ? marketingResourcesLink
    : Path.resellers(applicationId);
  const isAddResourcesLinkExternal = /^https?:\/\//i.test(addResourcesLink);

  const pageContainerClassName = "mx-auto w-full max-w-[1440px] space-y-8 pb-10";
  const surfaceClassName = "rounded-2xl border border-gray-100 bg-white p-6 shadow-sm";

  return (
    <main className={pageContainerClassName} aria-busy={isFetching} aria-live="polite">
      <header className={surfaceClassName} role="banner">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Manufacturer Portal
              </span>
              {isFetching && (
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <InlineSpinner size="sm" decorative />
                  Syncing
                </span>
              )}
            </div>
            <h1 className="text-[32px] font-semibold tracking-tight text-slate-900">Open orders</h1>
            <p className="max-w-2xl text-[15px] text-slate-600">
              Track every open line item across resellers, warehouses, and shipments in one place.
            </p>
          </div>

          <div className="flex flex-col gap-3 md:items-end">
            <DataRefreshBar
              align="right"
              lastRefreshedAt={lastRefreshedAt}
              isRefreshing={isFetching}
              onRefresh={handleRefresh}
              actions={(
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => sendMessageWithClear(assistantPrompt)}
                  className="rounded-xl"
                  aria-label="Open assistant with this page context"
                >
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            />

            <Button asChild variant="outline" className="rounded-xl">
              <Link to={Path.completedOrderDetails(applicationId)} aria-label="View completed order history">
                View completed orders
              </Link>
            </Button>
          </div>
        </div>

        <dl className="mt-6 grid gap-4 md:grid-cols-4">
          {metricCards.map(card => (
            <div key={card.label} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{card.label}</dt>
              <dd className={["mt-1 text-2xl font-semibold", card.tone].join(" ")}>{card.count}</dd>
              <dd className="mt-1 text-[13px] text-slate-500">
                <FormattedNumber value={card.amount} {...CurrencyUtils.format} />
              </dd>
            </div>
          ))}
        </dl>

        {isError && (
          <Alert variant="destructive" className="mt-6 rounded-2xl border border-red-100 bg-white">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            <AlertTitle className="text-slate-900">Couldn’t load open orders</AlertTitle>
            <AlertDescription className="text-slate-700">
              Try refreshing. If the problem persists, contact support.
            </AlertDescription>
          </Alert>
        )}
      </header>

      <section className={[surfaceClassName, "space-y-4"].join(" ")} aria-label="Search and filters">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by reseller, order number, PO, MPN, SKU, or location"
              className="h-11 w-full rounded-lg border-slate-200 bg-white pl-9 text-[15px]"
              aria-label="Search open orders"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-[color:var(--ops-accent)]"
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {statusFilterOptions.map(option => {
              const isActive = option.id === statusFilter;
              const className = [
                "rounded-xl",
                isActive ? "border-transparent bg-[var(--ops-accent)] text-white hover:bg-[var(--ops-accent)]/90" : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <Button
                  type="button"
                  key={option.id}
                  aria-pressed={isActive}
                  onClick={() => setStatusFilter(option.id)}
                  variant="outline"
                  size="sm"
                  className={className}
                >
                  {option.icon}
                  {option.label}
                </Button>
              );
            })}

            <Button
              type="button"
              aria-pressed={showOnlyBidOrders}
              onClick={() => setShowOnlyBidOrders(prev => !prev)}
              variant="outline"
              size="sm"
              className={[
                "rounded-xl",
                showOnlyBidOrders ? "border-transparent bg-[var(--ops-accent)] text-white hover:bg-[var(--ops-accent)]/90" : "",
              ].filter(Boolean).join(" ")}
            >
              <CircleDollarSign className="h-4 w-4" aria-hidden="true" />
              Bids
            </Button>

            <Button
              type="button"
              aria-pressed={showUniqueMpnsOnly}
              onClick={() => setShowUniqueMpnsOnly(prev => !prev)}
              variant="outline"
              size="sm"
              className={[
                "rounded-xl",
                showUniqueMpnsOnly ? "border-transparent bg-[var(--ops-accent)] text-white hover:bg-[var(--ops-accent)]/90" : "",
              ].filter(Boolean).join(" ")}
            >
              <Fingerprint className="h-4 w-4" aria-hidden="true" />
              Unique MPNs
            </Button>
          </div>
        </div>

        <p
          id={statusMessageId}
          role="status"
          className="text-[13px] text-slate-500"
          aria-live="polite"
        >
          {statusMessage}
        </p>
      </section>

      {isInitialLoad ? (
        <Card className="rounded-2xl border border-gray-100 bg-white shadow-sm">
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Progress />
            <p className="text-[15px] text-slate-600">Fetching the latest open orders…</p>
          </CardContent>
        </Card>
      ) : hasOrders ? (
        filteredOrders.length > 0 ? (
          <section className="space-y-4" aria-label="Open order details table">
            <div>
              <h2 className="text-[24px] font-semibold tracking-tight text-slate-900">Order line items</h2>
              <p className="text-[15px] text-slate-600">
                Sort, paginate, and drill into resellers, documents, and products.
              </p>
            </div>
            <OpenOrdersGrid productOrders={filteredOrders} withHeader withStatus variant="timeline" />
          </section>
        ) : (
          <Card className="rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-medium text-slate-900">No line items match what you’re looking for</CardTitle>
              <CardDescription className="text-[15px] text-slate-600">
                Update your filters or clear search to see every open line item again.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 text-[15px] text-slate-600">
              <div className="inline-flex items-center gap-2 rounded-xl border border-rose-100 bg-rose-50 px-3 py-2">
                <AlertCircle className="h-4 w-4 text-rose-600" aria-hidden="true" />
                Filters can be combined. Make sure you’re not limiting results unintentionally.
              </div>
              <Button
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                  setShowOnlyBidOrders(false);
                  setShowUniqueMpnsOnly(false);
                }}
                variant="outline"
                className="self-start rounded-xl"
              >
                Reset filters
              </Button>
            </CardContent>
          </Card>
        )
      ) : (
        <Card className="rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
          <CardHeader className="items-center space-y-3 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
              <Sparkles className="h-4 w-4" aria-hidden="true" /> No open orders yet
            </div>
            <CardTitle className="text-2xl font-semibold text-slate-900">Set up reseller readiness</CardTitle>
            <CardDescription className="text-[15px] text-slate-600">
              Add marketing resources and start conversations so resellers can confidently list and start ordering.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4 text-center">
            <div className="flex flex-wrap justify-center gap-3">
              <Button asChild className="rounded-xl">
                {isAddResourcesLinkExternal ? (
                  <a
                    href={addResourcesLink}
                    aria-label="Add reseller resources"
                    className="flex items-center gap-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Add reseller resources
                  </a>
                ) : (
                  <Link
                    to={addResourcesLink}
                    aria-label="Add reseller resources"
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Add reseller resources
                  </Link>
                )}
              </Button>
              <Button asChild variant="outline" className="rounded-xl">
                <Link to={Path.chat(applicationId)} aria-label="Chat with resellers" className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" aria-hidden="true" />
                  Chat with resellers
                </Link>
              </Button>
            </div>
            <p className="text-xs text-slate-500">You can fine-tune automation and qualification later.</p>
          </CardContent>
        </Card>
      )}
    </main>
  );
};

export default withRoot(OrderDetails);
